import { supabase, adminSupabase } from './supabaseService';
import type { User, UserSession } from '../types/supabase';

export const userService = {
    /**
     * Creates a new user or updates an existing one when they connect their wallet
     * @param walletAddress The wallet address
     * @param walletProvider The wallet provider ('phantom', 'solflare', etc.)
     * @param options Additional options
     */
    async createOrUpdateUser(
        walletAddress: string,
        walletProvider: string,
        options: { incrementLoginCount?: boolean } = { incrementLoginCount: true }
    ): Promise<User | null> {
        try {
            console.log(`Creating or updating user with wallet: ${walletAddress}, provider: ${walletProvider}`);
            console.log(`Options: incrementLoginCount=${options.incrementLoginCount}`);

            // Check if user exists
            const { data: existingUser, error: fetchError } = await adminSupabase
                .from('users')
                .select('*')
                .eq('wallet_address', walletAddress)
                .single();

            if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
                console.error('Error checking for existing user:', fetchError);
                return null;
            }

            if (existingUser) {
                console.log('Updating existing user:', existingUser.id);

                // Update existing user's last login and increment login count if requested
                const updateData: any = {
                    last_login: new Date().toISOString(),
                    wallet_provider: walletProvider,
                    updated_at: new Date().toISOString()
                };

                // Only increment login count if specifically requested
                if (options.incrementLoginCount) {
                    updateData.login_count = (existingUser.login_count || 0) + 1;
                }

                const { data: updatedUser, error: updateError } = await adminSupabase
                    .from('users')
                    .update(updateData)
                    .eq('wallet_address', walletAddress)
                    .select()
                    .single();

                if (updateError) {
                    console.error('Error updating user:', updateError);
                    return null;
                }

                // Create new session
                await this.createUserSession(updatedUser.id, walletProvider);

                return updatedUser;
            } else {
                console.log('Creating new user for wallet:', walletAddress);
                // Create new user
                const { data: newUser, error: insertError } = await adminSupabase
                    .from('users')
                    .insert({
                        wallet_address: walletAddress,
                        wallet_provider: walletProvider,
                        last_login: new Date().toISOString(),
                        login_count: 1
                    })
                    .select()
                    .single();

                if (insertError) {
                    console.error('Error creating user:', insertError.message);
                    console.error('Full error:', insertError);
                    return null;
                }

                console.log('New user created:', newUser?.id);

                // Create initial session if user was created
                if (newUser) {
                    await this.createUserSession(newUser.id, walletProvider);
                }

                return newUser;
            }
        } catch (error) {
            console.error('Error in createOrUpdateUser:', error);
            return null;
        }
    },

    /**
     * Creates a new user session
     */
    async createUserSession(userId: string, walletProvider: string): Promise<UserSession | null> {
        try {
            console.log('Creating user session for user:', userId);
            // Get basic device info
            const deviceInfo = {
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                language: navigator.language
            };

            // Get IP address (simplified - in production you might use a service)
            let ipAddress = 'unknown';
            try {
                const response = await fetch('https://api.ipify.org?format=json');
                const data = await response.json();
                ipAddress = data.ip;
            } catch (e) {
                console.warn('Could not fetch IP address:', e);
            }

            const { data, error } = await adminSupabase
                .from('user_sessions')
                .insert({
                    user_id: userId,
                    wallet_provider: walletProvider,
                    device_info: deviceInfo,
                    ip_address: ipAddress
                })
                .select()
                .single();

            if (error) {
                console.error('Error creating user session:', error);
                return null;
            }

            console.log('User session created:', data?.id);
            return data;
        } catch (error) {
            console.error('Error in createUserSession:', error);
            return null;
        }
    },

    /**
     * End a user session
     */
    async endUserSession(userId: string): Promise<void> {
        try {
            console.log('Ending session for user:', userId);
            // Find the most recent active session
            const { data: activeSessions } = await adminSupabase
                .from('user_sessions')
                .select('*')
                .eq('user_id', userId)
                .is('session_end', null)
                .order('session_start', { ascending: false })
                .limit(1);

            if (activeSessions && activeSessions.length > 0) {
                console.log('Found active session to end:', activeSessions[0].id);
                await adminSupabase
                    .from('user_sessions')
                    .update({ session_end: new Date().toISOString() })
                    .eq('id', activeSessions[0].id);
            } else {
                console.log('No active sessions found to end');
            }
        } catch (error) {
            console.error('Error ending user session:', error);
        }
    },

    /**
     * Get user by wallet address
     */
    async getUserByWallet(walletAddress: string): Promise<User | null> {
        try {
            const { data, error } = await adminSupabase
                .from('users')
                .select('*')
                .eq('wallet_address', walletAddress)
                .single();

            if (error) {
                if (error.code !== 'PGRST116') { // Not Found error
                    console.error('Error fetching user:', error);
                }
                return null;
            }

            return data;
        } catch (error) {
            console.error('Error in getUserByWallet:', error);
            return null;
        }
    },

    /**
     * Update user preferences
     */
    async updateUserPreferences(userId: string, preferences: object): Promise<User | null> {
        try {
            const { data, error } = await adminSupabase
                .from('users')
                .update({ user_preferences: preferences })
                .eq('id', userId)
                .select()
                .single();

            if (error) {
                console.error('Error updating user preferences:', error);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Error in updateUserPreferences:', error);
            return null;
        }
    }
};

export default userService;
